import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { refreshSession, setAccessToken, setSessionLostHandler, setActiveSectionHeader } from '@/lib/api';
import { authApi } from './auth.api';
import { useUiStore } from '@/stores/ui.store';

const AuthContext = React.createContext(null);

/** 15-minute access tokens (docs/03 §6); renew at 12 to leave room for a slow network. */
const SILENT_REFRESH_MS = 12 * 60 * 1000;

export const AUTH_STATUS = {
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  ANONYMOUS: 'anonymous',
};

/**
 * Session lifecycle.
 *
 * On boot the app has no access token — it lives in memory and the page just reloaded —
 * so it spends one round trip exchanging the refresh cookie for a fresh pair. That call
 * failing is the *normal* path for a visitor who is not signed in, so it resolves to
 * `anonymous` rather than surfacing an error.
 *
 * From then on a timer renews the token before it expires. The 401 interceptor in
 * `lib/api.js` is the safety net for a sleeping laptop, not the primary mechanism:
 * relying on it alone means the user's first click after lunch pays for a retry.
 */
export const AuthProvider = ({ children, initialUser = null }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = React.useState(initialUser);
  const [status, setStatus] = React.useState(initialUser ? AUTH_STATUS.AUTHENTICATED : AUTH_STATUS.LOADING);

  const activeSectionId = useUiStore((state) => state.activeSectionId);
  const setActiveSection = useUiStore((state) => state.setActiveSection);

  /** Wipe every trace of the session — token, user, and all cached server data. */
  const clearSession = React.useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus(AUTH_STATUS.ANONYMOUS);
    queryClient.clear();
  }, [queryClient]);

  // A 401 that survives the refresh attempt means the family was revoked or expired.
  React.useEffect(() => {
    setSessionLostHandler(clearSession);
    return () => setSessionLostHandler(null);
  }, [clearSession]);

  // The active section rides along on every request as a narrowing hint (docs/03 §5).
  React.useEffect(() => {
    setActiveSectionHeader(activeSectionId);
  }, [activeSectionId]);

  const applySession = React.useCallback(
    (nextUser) => {
      setUser(nextUser);
      setStatus(AUTH_STATUS.AUTHENTICATED);

      /*
       * Land the user somewhere they can actually see. A section-scoped user whose
       * persisted `activeSectionId` is a section they no longer hold would otherwise
       * send `X-Section-Id` for a section the server will refuse.
       */
      const allowed = nextUser?.access?.sectionIds || [];
      const hasGlobal = nextUser?.access?.hasGlobalScope;
      const persisted = useUiStore.getState().activeSectionId;

      if (persisted && !allowed.includes(persisted) && !hasGlobal) {
        setActiveSection(allowed[0] ?? null);
      } else if (!persisted && !hasGlobal && allowed.length) {
        setActiveSection(nextUser.defaultSectionId || allowed[0]);
      }
    },
    [setActiveSection],
  );

  // Boot: try to restore the session from the refresh cookie.
  React.useEffect(() => {
    if (initialUser) return undefined;

    let cancelled = false;
    refreshSession()
      .then(({ user: restored }) => {
        if (!cancelled) applySession(restored);
      })
      .catch(() => {
        if (!cancelled) {
          setAccessToken(null);
          setStatus(AUTH_STATUS.ANONYMOUS);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applySession, initialUser]);

  // Silent renewal while signed in.
  React.useEffect(() => {
    if (status !== AUTH_STATUS.AUTHENTICATED) return undefined;

    const timer = setInterval(() => {
      refreshSession().catch(() => {
        /* the interceptor and the next request will handle a hard failure */
      });
    }, SILENT_REFRESH_MS);

    return () => clearInterval(timer);
  }, [status]);

  const login = React.useCallback(
    async (credentials) => {
      const result = await authApi.login(credentials);
      setAccessToken(result.accessToken);
      applySession(result.user);
      return result;
    },
    [applySession],
  );

  const logout = React.useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Even if the revoke call fails, this device forgets the session.
      clearSession();
    }
  }, [clearSession]);

  /** Re-read `/auth/me` after an assignment change so permissions update in place. */
  const reloadUser = React.useCallback(async () => {
    const fresh = await authApi.me();
    applySession(fresh);
    return fresh;
  }, [applySession]);

  const value = React.useMemo(
    () => ({
      user,
      access: user?.access ?? null,
      status,
      isAuthenticated: status === AUTH_STATUS.AUTHENTICATED,
      isLoading: status === AUTH_STATUS.LOADING,
      login,
      logout,
      reloadUser,
    }),
    [user, status, login, logout, reloadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
};

export default AuthContext;
